/**
 * 「最近使用功能」的跳转地址：回到该功能「上一次」打开的页面（含查询参数）。
 *
 * 概览页的快捷入口数据来自后端 lastVisit，一个功能只有一条（按名称），记下的 appUrl 不一定带查询参数，
 * 也不一定是最近一次的。而很多页面（如「添加内容」/np/app/detail/:appId）必须靠 appCode、tid 等查询参数才能取到数据，
 * 并且内容菜单下每个模块的「添加」按钮跳到的 appId、tid 都不同，缺了参数就会请求 400、页面空白。
 * 所以路由守卫每次跳转时，在浏览器本地按「租户实例 + 功能名称（路由标题）」记下最近一次的完整地址，
 * 快捷入口点击时优先用它。
 */
import { getAccessInstance } from '@/utils/access_token';

const KEY_PREFIX = 'ce-recent-visit-urls:';
const MAX_ENTRIES = 50;

function storageKey() {
    return KEY_PREFIX + (getAccessInstance() || '');
}

function read() {
    try {
        return JSON.parse(localStorage.getItem(storageKey())) || {};
    } catch (e) {
        return {};
    }
}

/**
 * 记下一次访问。没有查询参数的地址不用记（本来就完整）；
 * 编辑某条具体内容（isEdit=true）的地址也不记，否则「添加内容」快捷入口会变成打开那条内容的编辑页
 */
export function rememberVisit(title, fullUrl, query = {}) {
    if (!title || !fullUrl || fullUrl.indexOf('?') === -1) return;
    if (String(query.isEdit) === 'true') return;
    try {
        const map = read();
        delete map[title]; // 删了再放，保证最近访问的排在最后
        map[title] = fullUrl;
        const titles = Object.keys(map);
        titles.slice(0, Math.max(0, titles.length - MAX_ENTRIES)).forEach(t => delete map[t]);
        localStorage.setItem(storageKey(), JSON.stringify(map));
    } catch (e) {
        // 隐私模式 / 存储已满：不影响跳转，只是快捷入口退回只用后端给的地址
    }
}

/** 「添加内容」页：缺了 tid 就取不到数据（请求 400、页面空白） */
export function isAddContentWithoutTid(url) {
    const [path, query = ''] = String(url || '').split('?');
    return /^\/np\/app\/detail\//.test(path) && !/(^|&)tid=[^&]+/.test(query);
}

// 兜底时按这个顺序找内容模块
const FALLBACK_MENU_TITLES = ['新闻资讯', '介绍内容'];

function findMenu(nodes, match) {
    for (const node of nodes || []) {
        if (match(node)) return node;
        const hit = findMenu(node.children, match);
        if (hit) return hit;
    }
    return null;
}

/**
 * 本地没有记录、也拿不到 tid 时的兜底：从内容菜单里找一个模块，拼出它的「添加内容」地址。
 * 内容菜单里每个数据源列表页的地址都形如 …naaCode{appCode}id{tid}，里面就有添加页需要的 appCode 和 tid。
 * 按 FALLBACK_MENU_TITLES 的顺序，取该分组下第一个这样的子菜单；都找不到返回 null。
 * menuTree 是 store.state.routes.menus（根节点 { children: [...] }，节点上有 meta.title、path、meta.path）
 */
export function buildAddContentUrl(menuTree) {
    const roots = (menuTree && menuTree.children) || [];
    for (const title of FALLBACK_MENU_TITLES) {
        const group = findMenu(roots, node => node.meta && node.meta.title === title && node.children && node.children.length);
        if (!group) continue;
        const source = findMenu(group.children, node => /naaCode(\d+)id(\d+)/i.test((node.meta && node.meta.path) || node.path || ''));
        if (!source) continue;
        const [, appCode, tid] = /naaCode(\d+)id(\d+)/i.exec((source.meta && source.meta.path) || source.path);
        return `/np/app/detail/${appCode}?appCode=${appCode}&tid=${tid}&goContent=true&isEdit=false&total=0`;
    }
    return null;
}

/**
 * 快捷入口要跳的地址（item 是 lastVisit 返回的一项：{ application, appUrl }）。
 * 优先用本地记的该功能最近一次的完整地址；没有就用后端给的 appUrl
 */
export function resolveVisitUrl(item) {
    const { application, appUrl } = item || {};
    return (application && read()[application]) || appUrl;
}
